import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useMemo,
} from 'react';
import {
  keepPreviousData,
  type QueryKey,
  queryOptions,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';

import type { FilterDefinition } from './useDeferredListFilters';
import type { PaginationConfig } from '@/components/DataTable/types';
import type { FilterConfig } from '@/components/ListFilters/types';
import type { SortingState } from '@tanstack/react-table';

import { HttpClient } from '@/network/HttpClient';

import { useDeferredListFilters } from './useDeferredListFilters';
import { useTablePagination } from './useTablePagination';

// --- Types ---

type ListResponse<TItem> = {
  data: TItem[];
  records_total: number;
};

type UseListConfig<TItem> = {
  /** Data fetching configuration — `url`, `queryKey`, plus any `useQuery` options */
  fetch: {
    /** API endpoint URL */
    url: string;
    /** React Query cache key (string or array) */
    queryKey: string | QueryKey;
    /** Additional `useQuery` options (e.g. `enabled`, `staleTime`, `gcTime`, `retry`, `refetchInterval`) */
    options?: Omit<
      UseQueryOptions<ListResponse<TItem> | undefined>,
      'queryKey' | 'queryFn' | 'placeholderData'
    >;
  };
  /** Pagination settings. Defaults to page 1, 100 items per page */
  pagination?: {
    /** Items per page. Default: `100` */
    pageSize?: number;
    /** Starting page. Default: `1` */
    initialPage?: number;
  };
  /** Initial sort column and direction */
  sorting?: {
    /** Sort field sent to the backend. Suggests `TItem` keys but accepts any backend field name (e.g. a non-column field like `created_at`) */
    id: (keyof TItem & string) | (string & {});
    /** Sort descending. Default: `false` */
    desc?: boolean;
  };
  /** Deferred filter configuration — applied only on button click */
  filters?: {
    /** Filter field definitions (select, multiselect, boolean, date, etc.) */
    definitions: FilterDefinition[];
    /** Initial filter values on page load */
    defaults?: Record<string, unknown>;
    /** localStorage key for filter persistence (opt-in, not recommended for new pages) */
    storageKey?: string;
    /**
     * Reshape applied filters before they are sent as query params (e.g. group
     * separate `from`/`till` date fields into `created_at: { from, till }`, or map
     * an autocomplete object to `[id]`). Wrap in `useCallback` to keep it stable.
     */
    transform?: (applied: Record<string, unknown>) => Record<string, unknown>;
  };
  /**
   * Debounced search string. When provided, adds `search: { value }` to query params.
   * Debounce externally with `useDebounce` before passing here.
   */
  search?: string;
};

/** Spread into `<DataTable>`: `{...table}` */
type UseListTableReturn = {
  /** Total records count (for `<TableWrapper recordsTotal={...}>`) */
  recordsTotal: number;
  /** Pagination config or `undefined` when no data */
  pagination: PaginationConfig | undefined;
  /** Current sorting state */
  sorting: SortingState;
  /** Sorting change handler */
  onSortingChange: Dispatch<SetStateAction<SortingState>>;
};

/** Spread into `<ListFilters>`: `{...filters}` */
type UseListFiltersReturn = {
  /** Filter configs for rendering */
  filters: FilterConfig[];
  /** Apply pending filters to the query */
  onApplyFilters: () => void;
  /** `true` when pending filters differ from applied */
  hasChanges: boolean;
};

type UseListReturn<TItem> = {
  /** Unpacked data array, never `undefined` */
  data: TItem[];
  /** `true` while fetching */
  isLoading: boolean;
  /** `true` if the query failed */
  isError: boolean;
  /** Error object or `null` */
  error: Error | null;
  /** Re-fetch data without changing filters */
  refetch: () => void;
  /** Spread into `<DataTable>` — pagination, sorting */
  table: UseListTableReturn;
  /** Spread into `<ListFilters>` — filter configs, apply button */
  filters: UseListFiltersReturn;
  /** Raw applied filters for custom transformation (e.g. date grouping) */
  appliedFilters: Record<string, unknown>;
};

// --- Hook ---

/**
 * All-in-one hook for list pages: data fetching, pagination, sorting, and filtering.
 *
 * Returns ready-to-spread objects for `<DataTable>` (`{...table}`)
 * and `<ListFilters>` (`{...filters}`), plus unpacked data array and error state.
 *
 * @example
 * ```tsx
 * const { data, isLoading, table, filters } = useList<ServiceListItem>({
 *   fetch: { url: fetch_url, queryKey: SERVICES_LIST_QUERY_KEY },
 *   pagination: { pageSize: 100 },
 *   sorting: { id: 'name' },
 *   filters: {
 *     definitions: [
 *       { type: 'boolean', name: 'active', label: t('Active') },
 *       { type: 'multiselect', name: 'locations', label: t('Locations'), options },
 *     ],
 *   },
 * })
 *
 * <ListFilters {...filters} />
 * <DataTable data={data} columns={columns} pending={isLoading} {...table} />
 * ```
 */
export const useList = <TItem>(
  config: UseListConfig<TItem>,
): UseListReturn<TItem> => {
  const { pagination, sorting } = useTablePagination({
    columnSort: config.sorting
      ? [{ id: config.sorting.id, desc: config.sorting.desc ?? false }]
      : [],
    pagination: {
      page: config.pagination?.initialPage ?? 1,
      size_per_page: config.pagination?.pageSize ?? 100,
    },
  });

  const {
    filtersConfig,
    appliedFilters,
    hasAppliedFilters,
    onApplyFilters: applyFilters,
    hasChanges,
  } = useDeferredListFilters({
    storageKey: config.filters?.storageKey,
    filterDefinitions: config.filters?.definitions ?? [],
    defaultFilters: config.filters?.defaults,
  });

  const { page, size_per_page, onPageChange, onSizePerPageChange } = pagination;
  const { sortParams } = sorting;

  const onApplyFilters = useCallback(() => {
    applyFilters();
    onPageChange(1);
  }, [applyFilters, onPageChange]);

  const transformFilters = config.filters?.transform;
  const filtersForQuery = useMemo(
    () =>
      transformFilters ? transformFilters(appliedFilters) : appliedFilters,
    [transformFilters, appliedFilters],
  );

  const queryParams = useMemo(
    () => ({
      page,
      size_per_page,
      ...sortParams,
      ...(config.search !== undefined
        ? { search: { value: config.search } }
        : {}),
      ...(hasAppliedFilters ? { filters: filtersForQuery } : {}),
    }),
    [
      page,
      size_per_page,
      sortParams,
      config.search,
      hasAppliedFilters,
      filtersForQuery,
    ],
  );

  const listQueryOptions = queryOptions({
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    retry: false,
    ...config.fetch.options,
    queryKey: [
      ...(Array.isArray(config.fetch.queryKey)
        ? config.fetch.queryKey
        : [config.fetch.queryKey]),
      config.fetch.url,
      queryParams,
    ],
    queryFn: () =>
      HttpClient.request<ListResponse<TItem>>({
        method: 'GET',
        url: config.fetch.url,
        params: queryParams,
      }),
  });

  const {
    data: responseData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery(listQueryOptions);

  const data = useMemo(() => responseData?.data ?? [], [responseData?.data]);
  const recordsTotal = responseData?.records_total ?? 0;

  const table = useMemo<UseListTableReturn>(
    () => ({
      recordsTotal,
      pagination:
        data.length > 0
          ? {
              page,
              sizePerPage: size_per_page,
              recordsTotal,
              onPageChange,
              onSizePerPageChange,
            }
          : undefined,
      sorting: sorting.state,
      onSortingChange: sorting.onChange,
    }),
    [
      recordsTotal,
      data.length,
      page,
      size_per_page,
      onPageChange,
      onSizePerPageChange,
      sorting.state,
      sorting.onChange,
    ],
  );

  const filters = useMemo<UseListFiltersReturn>(
    () => ({
      filters: filtersConfig,
      onApplyFilters,
      hasChanges,
    }),
    [filtersConfig, onApplyFilters, hasChanges],
  );

  return {
    data,
    isLoading,
    isError,
    error: error as Error | null,
    refetch,
    table,
    filters,
    appliedFilters,
  };
};
